import { useEffect, useState, type RefObject } from "react";
import {
  PauseIcon,
  PlayIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsLeftIcon,
  ChevronsRightIcon,
} from "./icons";
import { FRAME } from "../clipTrack";

// ════════════════════════════════════════════════════════════════════════
// FrameStepper — NEW (Coach Review v2 layout).
// ────────────────────────────────────────────────────────────────────────
// The transport cluster under the stage: back 1s / back 1 frame /
// play-pause / forward 1 frame / forward 1s. Drives the REAL <video>
// element (the telestrator's backing media) directly via its ref —
// not a mock playhead. The video's own `timeupdate` events feed
// `useVideoPlayhead` so every other readout stays in sync.
//
// Tactical-board mode has no video; the parent simply doesn't render
// this then. When `videoRef.current` is null (video not yet loaded)
// the buttons are disabled rather than throwing.
//
// Icons are inline SVG (see `./icons` — no new deps). The play/pause
// button is the accent-filled hero control; the step buttons are
// ghost. No new tokens.
// ════════════════════════════════════════════════════════════════════════

export type FrameStepperProps = {
  /** Ref to the telestrator's backing `<video>`. */
  videoRef: RefObject<HTMLVideoElement | null>;
};

export function FrameStepper({ videoRef }: FrameStepperProps) {
  // Mirror the element's paused state so the play/pause icon is
  // reactive — `<video>.paused` is non-reactive on its own.
  const [paused, setPaused] = useState(true);
  const [ready, setReady] = useState(false);
  const el = videoRef.current;

  useEffect(() => {
    if (!el) {
      setReady(false);
      setPaused(true);
      return;
    }
    const sync = () => {
      setPaused(el.paused);
      setReady(Number.isFinite(el.duration) && el.duration > 0);
    };
    sync();
    el.addEventListener("play", sync);
    el.addEventListener("pause", sync);
    el.addEventListener("loadedmetadata", sync);
    return () => {
      el.removeEventListener("play", sync);
      el.removeEventListener("pause", sync);
      el.removeEventListener("loadedmetadata", sync);
    };
  }, [el]);

  /** Seek the video by `delta` seconds, clamped to `[0, duration]`. */
  const step = (delta: number) => {
    const v = videoRef.current;
    if (!v) return;
    const dur = Number.isFinite(v.duration) ? v.duration : Infinity;
    v.currentTime = Math.max(0, Math.min(dur, v.currentTime + delta));
  };

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) void v.play().catch(() => undefined);
    else v.pause();
  };

  const stepBtn =
    "inline-flex h-7 w-7 items-center justify-center rounded text-ink-secondary " +
    "transition-colors hover:bg-field disabled:opacity-40 disabled:hover:bg-transparent";

  return (
    <div
      className="flex items-center gap-1"
      data-testid="coach-review-frame-stepper"
    >
      <button
        type="button"
        title="Back 1s · Shift+,"
        onClick={() => step(-1)}
        disabled={!ready}
        className={stepBtn}
        data-testid="frame-stepper-back-1s"
      >
        <ChevronsLeftIcon size={14} />
      </button>
      <button
        type="button"
        title="Back 1 frame · ,"
        onClick={() => step(-FRAME)}
        disabled={!ready}
        className={stepBtn}
        data-testid="frame-stepper-back-frame"
      >
        <ChevronLeftIcon size={14} />
      </button>
      <button
        type="button"
        title={paused ? "Play · Space" : "Pause · Space"}
        onClick={togglePlay}
        disabled={!ready}
        className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-accent text-accent-ink transition-colors hover:bg-accent-hover disabled:opacity-40"
        data-testid="frame-stepper-play"
        aria-label={paused ? "Play" : "Pause"}
      >
        {paused ? (
          <PlayIcon size={14} fill="currentColor" />
        ) : (
          <PauseIcon size={14} fill="currentColor" />
        )}
      </button>
      <button
        type="button"
        title="Forward 1 frame · ."
        onClick={() => step(FRAME)}
        disabled={!ready}
        className={stepBtn}
        data-testid="frame-stepper-forward-frame"
      >
        <ChevronRightIcon size={14} />
      </button>
      <button
        type="button"
        title="Forward 1s · Shift+."
        onClick={() => step(1)}
        disabled={!ready}
        className={stepBtn}
        data-testid="frame-stepper-forward-1s"
      >
        <ChevronsRightIcon size={14} />
      </button>
    </div>
  );
}
